import { useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "i18next";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000InputText from "../../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputCurrency from "../../../../../u2000Core/components/formControls/input/U2000InputCurrency";
import U2000InputCalendarCustom from "../../../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import U2000ComboBox from "../../../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import U2000Label from "../../../../../u2000Core/components/formControls/label/U2000Label";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U3125FactureNestedParentHandlers } from "../u3125FactureInvoiceLinesTab/U3125InvoiceLineHandlers";
import type { U3125PaymentHandlerReturn } from "./U3125PaymentHandlers";
import type { U3125PaymentTypeInitItem, U3125TaxRateInitItem } from "../../../../U3125Service";
import {
  pickPaymentTypeLabel,
  u3125AllocatePaymentFromReceivedAmount,
  u3125CanManagePayments,
  u3125FormatPaymentRemarkForDisplay,
  u3125IsCreditApplicationAutoPaymentRemark,
  u3125PaymentComponentsTotal,
  u3125PaymentRemainingBalanceForInvoice,
  u3125SelectablePaymentTypesForPayment,
  u3125SuggestPaymentTypeCode,
  u3125TaxAmountFieldLabels,
} from "../../../../U3125Service";
import { u3125FormatCurrency } from "../../../../U3125";

interface U3125PaymentEditDlgProps {
  u3125PaymentHandler: U3125PaymentHandlerReturn;
  parentFormHandlers: U3125FactureNestedParentHandlers;
  paymentTypes: U3125PaymentTypeInitItem[];
  taxRates: U3125TaxRateInitItem[];
  languageCode: string;
  t: TFunction;
  NS: string;
}

function U3125PaymentEditDlg({
  u3125PaymentHandler,
  parentFormHandlers,
  paymentTypes,
  taxRates,
  languageCode,
  t,
  NS,
}: U3125PaymentEditDlgProps) {
  const { formStateHandlers, onDlgClose, isDialogOpen } = u3125PaymentHandler;
  const payData = formStateHandlers.getData();
  const invoice = parentFormHandlers.getData?.() ?? null;
  const currentCode = payData?.paymentTypeCode?.trim() ?? "";
  const [adjustBreakdown, setAdjustBreakdown] = useState(false);
  const skipAutoAllocateRef = useRef(false);
  const initializedAddRef = useRef(false);

  const documentNature = invoice?.documentNature ?? "STANDARD";
  const receivedAmount = Number(payData?.amountTotal ?? 0);

  const nestedNone = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.NONE;
  const isAddMode = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD;
  const canManagePayments = u3125CanManagePayments(invoice?.status);
  const isAutoAdvancePayment = u3125IsCreditApplicationAutoPaymentRemark(payData?.remark);
  const readOnly = nestedNone || !canManagePayments || isAutoAdvancePayment;
  const breakdownReadOnly = readOnly || !adjustBreakdown;

  const remainingBalance = useMemo(() => {
    if (!invoice) {
      return { amountTaxable: 0, amountNonTaxable: 0, taxAmount1: 0, taxAmount2: 0 };
    }
    return u3125PaymentRemainingBalanceForInvoice(
      invoice,
      invoice.payments ?? [],
      taxRates,
      payData?.id?.trim() || undefined,
    );
  }, [
    invoice,
    invoice?.amountInvoiceTaxable,
    invoice?.amountInvoiceNonTaxable,
    invoice?.taxAmount1,
    invoice?.taxAmount2,
    invoice?.creditApplications,
    invoice?.payments,
    payData?.id,
    taxRates,
  ]);

  const remainingGrandTotal = useMemo(() => u3125PaymentComponentsTotal(remainingBalance), [remainingBalance]);

  const suggestedPaymentTypeCode = useMemo(
    () => u3125SuggestPaymentTypeCode(documentNature, receivedAmount, remainingGrandTotal),
    [documentNature, receivedAmount, remainingGrandTotal],
  );

  const selectablePaymentTypes = useMemo(
    () =>
      u3125SelectablePaymentTypesForPayment(paymentTypes, documentNature, {
        currentCode,
        receivedAmount,
        remainingGrandTotal,
      }),
    [paymentTypes, documentNature, currentCode, receivedAmount, remainingGrandTotal],
  );

  const taxAmountLabels = useMemo(() => {
    return u3125TaxAmountFieldLabels(taxRates, invoice?.taxRateCode, languageCode, {
      taxAmount1: t("lblTaxAmount1", { ns: NS }),
      taxAmount2: t("lblTaxAmount2", { ns: NS }),
    });
  }, [invoice?.taxRateCode, taxRates, languageCode, t, NS]);

  const applyAllocation = (amountReceived: number) => {
    if (!invoice || readOnly || adjustBreakdown) return;
    skipAutoAllocateRef.current = true;
    const current = formStateHandlers.getData();
    if (!current) return;
    const allocated = u3125AllocatePaymentFromReceivedAmount(amountReceived, remainingBalance, invoice.customerTaxable);
    const paymentTypeCode =
      u3125SuggestPaymentTypeCode(documentNature, amountReceived, remainingGrandTotal) ?? current.paymentTypeCode;
    formStateHandlers.setData({
      ...current,
      paymentTypeCode,
      amountTotal: allocated.amountTotal,
      amountTaxable: allocated.amountTaxable,
      amountNonTaxable: allocated.amountNonTaxable,
      taxAmount1: allocated.taxAmount1,
      taxAmount2: allocated.taxAmount2,
    });
    window.setTimeout(() => {
      skipAutoAllocateRef.current = false;
    }, 0);
  };

  useEffect(() => {
    if (!isDialogOpen || readOnly || !suggestedPaymentTypeCode) return;
    const current = formStateHandlers.getData();
    if (!current || current.paymentTypeCode?.trim().toUpperCase() === suggestedPaymentTypeCode) return;
    formStateHandlers.setData({ ...current, paymentTypeCode: suggestedPaymentTypeCode });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- synchro type selon montant reçu / solde
  }, [isDialogOpen, readOnly, suggestedPaymentTypeCode]);

  useEffect(() => {
    if (!isDialogOpen) {
      setAdjustBreakdown(false);
      initializedAddRef.current = false;
      return;
    }
    if (readOnly || !isAddMode || initializedAddRef.current) return;
    if (remainingGrandTotal <= 0.005) return;
    initializedAddRef.current = true;
    applyAllocation(remainingGrandTotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init à l'ouverture ADD quand le solde est disponible
  }, [isDialogOpen, isAddMode, readOnly, remainingGrandTotal, invoice?.id, invoice?.creditApplications, invoice?.payments]);

  useEffect(() => {
    if (!isDialogOpen || readOnly || adjustBreakdown || skipAutoAllocateRef.current) return;
    const received = Number(payData?.amountTotal ?? 0);
    const expected = u3125AllocatePaymentFromReceivedAmount(received, remainingBalance, invoice?.customerTaxable);
    const currentTaxable = Number(payData?.amountTaxable ?? 0);
    const currentNonTaxable = Number(payData?.amountNonTaxable ?? 0);
    const currentTax1 = Number(payData?.taxAmount1 ?? 0);
    const currentTax2 = Number(payData?.taxAmount2 ?? 0);
    const matches =
      currentTaxable === expected.amountTaxable &&
      currentNonTaxable === expected.amountNonTaxable &&
      currentTax1 === expected.taxAmount1 &&
      currentTax2 === expected.taxAmount2;
    if (matches) return;
    applyAllocation(received);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- realignement sur montant reçu
  }, [payData?.amountTotal, isDialogOpen, readOnly, adjustBreakdown, remainingBalance, invoice?.customerTaxable]);

  const handleClose = (result: string) => {
    if (result === "cancel") {
      onDlgClose("cancel");
    } else {
      onDlgClose("save");
    }
  };

  const handleRecalculate = () => {
    setAdjustBreakdown(false);
    applyAllocation(Number(payData?.amountTotal ?? 0));
  };

  return (
    <U2000SaveCancelDialog
      isOpen={isDialogOpen}
      onClose={handleClose}
      title={t("sectionPayments", { ns: NS })}
      isValid={true}
      readOnly={readOnly}
    >
      <div className="flex flex-col gap-y-4">
        {isAutoAdvancePayment ? (
          <p className="text-sm text-muted-foreground">{t("msgAdvanceAutoPaymentReadonly", { ns: NS })}</p>
        ) : null}
        <U2000InputCalendarCustom formStateHandlers={formStateHandlers} fieldName="paymentDate" label={t("lblPaymentDate", { ns: NS })} readonly={readOnly} dateFormat="yyyy-MM-dd" />
        <U2000ComboBox
          formStateHandlers={formStateHandlers as any}
          fieldName="paymentTypeCode"
          label={t("lblPaymentTypeCode", { ns: NS })}
          data={selectablePaymentTypes}
          keyField="code"
          descField="code"
          required
          readonly={readOnly || suggestedPaymentTypeCode != null}
          noneItemLabel={t("lblSelect", { ns: NS })}
          getValue={(it) => it.code}
          findItemByValue={(value, list) => {
            if (value == null || value === "") return null;
            return list.find((it) => it.code === String(value)) ?? null;
          }}
          formatLabel={(it) => `${pickPaymentTypeLabel(paymentTypes, it.code, languageCode)} (${it.code})`}
        />

        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <U2000Label label={t("lblPaymentAmountReceived", { ns: NS })} htmlFor="amountTotal" />
            {!readOnly ? (
              <span className="text-xs text-gray-600">
                {t("lblPaymentRemainingBalance", { amount: u3125FormatCurrency(remainingGrandTotal), ns: NS })}
              </span>
            ) : null}
          </div>
          <U2000InputCurrency
            formStateHandlers={formStateHandlers}
            fieldName="amountTotal"
            label=""
            readonly={readOnly}
            decimalDigits={2}
            allowNegative={false}
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-muted/40 p-3">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium text-gray-700">{t("lblPaymentBreakdown", { ns: NS })}</span>
            {!readOnly ? (
              <div className="flex flex-wrap gap-2">
                {!adjustBreakdown ? (
                  <U2000ActionBtn
                    label={t("btnPaymentAdjustBreakdown", { ns: NS })}
                    handleClick={() => setAdjustBreakdown(true)}
                    disabled={false}
                    hasRole={true}
                  />
                ) : (
                  <>
                    <U2000ActionBtn
                      label={t("btnPaymentRecalculateBreakdown", { ns: NS })}
                      handleClick={handleRecalculate}
                      disabled={false}
                      hasRole={true}
                    />
                  </>
                )}
              </div>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <U2000InputCurrency
              fieldName="amountTaxable"
              label={t("lblAmountTaxable", { ns: NS })}
              formStateHandlers={formStateHandlers}
              readonly={breakdownReadOnly}
              decimalDigits={2}
              allowNegative={false}
            />
            <U2000InputCurrency
              fieldName="amountNonTaxable"
              label={t("lblAmountNonTaxable", { ns: NS })}
              formStateHandlers={formStateHandlers}
              readonly={breakdownReadOnly}
              decimalDigits={2}
              allowNegative={false}
            />
            <U2000InputCurrency
              fieldName="taxAmount1"
              label={taxAmountLabels.taxAmount1}
              formStateHandlers={formStateHandlers}
              readonly={breakdownReadOnly}
              decimalDigits={2}
              allowNegative={false}
            />
            <U2000InputCurrency
              fieldName="taxAmount2"
              label={taxAmountLabels.taxAmount2}
              formStateHandlers={formStateHandlers}
              readonly={breakdownReadOnly}
              decimalDigits={2}
              allowNegative={false}
            />
          </div>
        </div>

        {isAutoAdvancePayment ? (
          <div className="space-y-1">
            <U2000Label label={t("lblRemark", { ns: NS })} htmlFor="remark" />
            <p className="text-sm text-foreground">{u3125FormatPaymentRemarkForDisplay(payData?.remark)}</p>
          </div>
        ) : (
          <U2000InputText fieldName="remark" label={t("lblRemark", { ns: NS })} formStateHandlers={formStateHandlers} readonly={readOnly} />
        )}
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3125PaymentEditDlg;
